/**
 * Ring 2 Shift Widget — "This week on your shifts" (Phase 25.5)
 *
 * Manager-facing dashboard widget that lists Ring 2 fanouts grouped by
 * (Section × Shift × Zone × variant). Each row shows completion progress
 * inline and opens a side panel with per-peer status on click.
 *
 * Visible to manager+ (gated at the Dashboard page level).
 */

import { useEffect, useState } from 'react';
import {
  Users2,
  X,
  CheckCircle2,
  Clock,
  CircleDashed,
  AlertTriangle,
  GraduationCap,
  Target,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useRing2Summary,
  type Ring2Fanout,
  type Ring2Peer,
  type Ring2Range,
} from '../../hooks/useRing2Summary';

interface Ring2ShiftWidgetProps {
  className?: string;
}

export function Ring2ShiftWidget({ className = '' }: Ring2ShiftWidgetProps) {
  const [range, setRange] = useState<Ring2Range>('this-week');
  const [selected, setSelected] = useState<Ring2Fanout | null>(null);

  const { data, isLoading, isError } = useRing2Summary(range);

  const fanouts = data?.fanouts ?? [];

  return (
    <div
      className={`rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden ${className}`}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-guild-mint/50">
            <GraduationCap size={18} className="text-guild-green" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              This week on your shifts
            </h2>
            <p className="text-xs text-gray-500">
              Team refreshers fanned out after low-score assessments
            </p>
          </div>
        </div>
        <RangeToggle value={range} onChange={setRange} />
      </div>

      <div className="p-4">
        {isLoading ? (
          <LoadingRows />
        ) : isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load team refreshers"
            description="Please retry in a moment."
          />
        ) : fanouts.length === 0 ? (
          <EmptyState
            icon={Users2}
            title="No team refreshers this week on your shifts."
            description="When a low score triggers a section refresher, it will show up here for you to follow up with the shift."
          />
        ) : (
          <ul className="space-y-2">
            {fanouts.map((f) => (
              <li key={f.fanoutGroupId}>
                <FanoutRow fanout={f} onClick={() => setSelected(f)} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <DrillDownPanel
          fanout={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ============================================
// RANGE TOGGLE
// ============================================

interface RangeToggleProps {
  value: Ring2Range;
  onChange: (value: Ring2Range) => void;
}

function RangeToggle({ value, onChange }: RangeToggleProps) {
  const options: Array<{ value: Ring2Range; label: string }> = [
    { value: 'this-week', label: 'This week' },
    { value: 'last-week', label: 'Last week' },
    { value: 'last-30-days', label: 'Last 30 days' },
  ];

  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-medium">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 rounded-md transition-colors ${
            value === opt.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// FANOUT ROW
// ============================================

interface FanoutRowProps {
  fanout: Ring2Fanout;
  onClick: () => void;
}

function FanoutRow({ fanout, onClick }: FanoutRowProps) {
  const total = fanout.assignedCount;
  const completed = fanout.completedCount;
  const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const rateColor =
    rate >= 80
      ? 'bg-emerald-500'
      : rate >= 50
        ? 'bg-amber-500'
        : 'bg-red-500';

  const sectionLabel =
    fanout.sectionTitle ?? fanout.materialTitle ?? 'Section refresher';
  const shiftLabel = fanout.shiftName ?? 'Unassigned shift';
  const zoneLabel = fanout.stationName ?? 'No zone';
  const variantBadge = getVariantBadge(fanout.variant);
  const VariantIcon = variantBadge.icon;
  const firedDate = formatShortDate(fanout.firedAt);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-xl p-4 text-left transition-colors hover:bg-gray-50 border border-transparent hover:border-gray-100"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {sectionLabel}
            </p>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${variantBadge.badge}`}
            >
              <VariantIcon size={10} />
              {variantBadge.label}
            </span>
          </div>
          {fanout.sopTitle && (
            <p className="mt-0.5 text-xs text-gray-500 truncate">
              {fanout.sopTitle}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
            <span>
              <span className="font-medium text-gray-700">{shiftLabel}</span>
              <span className="text-gray-300 mx-1.5">·</span>
              <span>{zoneLabel}</span>
            </span>
            <span className="text-gray-300">·</span>
            <span>
              {total} operator{total === 1 ? '' : 's'} assigned {firedDate}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-gray-900">
            {completed}/{total}
          </p>
          <p className="text-xs text-gray-500">{rate}% done</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${rateColor}`}
          style={{ width: `${rate}%` }}
        />
      </div>
    </button>
  );
}

// ============================================
// DRILL-DOWN PANEL
// ============================================

interface DrillDownPanelProps {
  fanout: Ring2Fanout;
  onClose: () => void;
}

function DrillDownPanel({ fanout, onClose }: DrillDownPanelProps) {
  const variantBadge = getVariantBadge(fanout.variant);
  const VariantIcon = variantBadge.icon;

  // Esc-to-close — matches project modal pattern (SOPUploadModal, KpiBuilderModal, etc.)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ring2-panel-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="flex-1 bg-gray-900/40 backdrop-blur-sm"
      />
      {/* Panel */}
      <div className="w-full max-w-md bg-white shadow-xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                id="ring2-panel-title"
                className="text-base font-semibold text-gray-900"
              >
                {fanout.sectionTitle ?? fanout.materialTitle ?? 'Refresher rollout'}
              </h3>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${variantBadge.badge}`}
              >
                <VariantIcon size={10} />
                {variantBadge.label}
              </span>
            </div>
            {fanout.sopTitle && (
              <p className="mt-0.5 text-xs text-gray-500">{fanout.sopTitle}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {fanout.shiftName ?? 'Unassigned shift'}
              <span className="text-gray-300 mx-1.5">·</span>
              {fanout.stationName ?? 'No zone'}
              <span className="text-gray-300 mx-1.5">·</span>
              Fired {formatShortDate(fanout.firedAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Source operator (Ring 1) */}
        <div className="px-6 py-4 bg-guild-mint/20 border-b border-gray-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Source operator (Ring 1)
          </p>
          {fanout.sourceOperatorName ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {fanout.sourceOperatorName}
                </p>
                {fanout.sourceOperatorEmployeeId && (
                  <p className="text-xs text-gray-500">
                    ID: {fanout.sourceOperatorEmployeeId}
                  </p>
                )}
              </div>
              <StatusBadge status={fanout.ring1Status} />
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">
              Unable to resolve source operator — Ring 1 bundle may have been
              archived.
            </p>
          )}
        </div>

        {/* Peer table */}
        <div className="px-6 py-4 flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Peers assigned ({fanout.assignedCount})
            </p>
            <p className="text-xs text-gray-500">
              {fanout.completedCount}/{fanout.assignedCount} completed
            </p>
          </div>
          {fanout.peers.length === 0 ? (
            <p className="text-xs text-gray-500 italic py-4 text-center">
              No peer assignments yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {fanout.peers.map((p) => (
                <li key={p.assignmentId} className="py-3">
                  <PeerRow peer={p} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// PEER ROW
// ============================================

function PeerRow({ peer }: { peer: Ring2Peer }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {peer.firstName} {peer.lastName}
        </p>
        <p className="text-xs text-gray-400">
          ID: {peer.employeeId}
          <span className="text-gray-300 mx-1.5">·</span>
          Assigned {formatShortDate(peer.assignedAt)}
          {peer.completedAt && (
            <>
              <span className="text-gray-300 mx-1.5">·</span>
              Completed {formatShortDate(peer.completedAt)}
            </>
          )}
        </p>
      </div>
      <StatusBadge status={peer.status} />
    </div>
  );
}

// ============================================
// STATUS BADGE
// ============================================

function StatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
        —
      </span>
    );
  }

  const map: Record<
    string,
    { label: string; classes: string; icon: LucideIcon }
  > = {
    completed: {
      label: 'Completed',
      classes: 'bg-emerald-50 text-emerald-700',
      icon: CheckCircle2,
    },
    in_progress: {
      label: 'In progress',
      classes: 'bg-blue-50 text-blue-700',
      icon: Clock,
    },
    assigned: {
      label: 'Assigned',
      classes: 'bg-gray-100 text-gray-600',
      icon: CircleDashed,
    },
    overdue: {
      label: 'Overdue',
      classes: 'bg-red-50 text-red-700',
      icon: AlertTriangle,
    },
  };

  const entry = map[status] ?? {
    label: status,
    classes: 'bg-gray-100 text-gray-600',
    icon: CircleDashed,
  };
  const Icon = entry.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${entry.classes}`}
    >
      <Icon size={10} />
      {entry.label}
    </span>
  );
}

// ============================================
// HELPERS
// ============================================

function getVariantBadge(variant: Ring2Fanout['variant']) {
  if (variant === 'focused_drill') {
    return {
      label: 'Focused drill',
      badge: 'bg-red-50 text-red-700',
      icon: Target,
    };
  }
  return {
    label: 'Refresher',
    badge: 'bg-blue-50 text-blue-700',
    icon: GraduationCap,
  };
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

// ============================================
// SUB-COMPONENTS
// ============================================

function LoadingRows() {
  return (
    <div className="space-y-2" aria-busy="true">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="rounded-xl p-4 border border-transparent animate-pulse"
        >
          <div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
          <div className="h-3 bg-gray-100 rounded w-1/2 mb-3" />
          <div className="h-1.5 bg-gray-100 rounded-full" />
        </div>
      ))}
    </div>
  );
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
}

function EmptyState({ icon: Icon, title, description }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 mb-4">
        <Icon size={24} className="text-gray-300" />
      </div>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-400 max-w-xs">{description}</p>
    </div>
  );
}

export default Ring2ShiftWidget;
